import { addWeeks, differenceInDays } from 'date-fns';

// NHS Childhood Vaccination Schedule
export const nhsSchedule = [
  { ageWeeks: 8, vaccines: [
    { name: '6-in-1 (Dose 1)', desc: 'Diphtheria, tetanus, whooping cough, polio, Hib, hepatitis B' },
    { name: 'Rotavirus (Dose 1)', desc: 'Oral vaccine for rotavirus gastroenteritis' },
    { name: 'MenB (Dose 1)', desc: 'Meningococcal group B' },
  ]},
  { ageWeeks: 12, vaccines: [
    { name: '6-in-1 (Dose 2)', desc: 'Diphtheria, tetanus, whooping cough, polio, Hib, hepatitis B' },
    { name: 'PCV (Dose 1)', desc: 'Pneumococcal conjugate vaccine' },
    { name: 'Rotavirus (Dose 2)', desc: 'Oral vaccine for rotavirus gastroenteritis' },
  ]},
  { ageWeeks: 16, vaccines: [
    { name: '6-in-1 (Dose 3)', desc: 'Diphtheria, tetanus, whooping cough, polio, Hib, hepatitis B' },
    { name: 'MenB (Dose 2)', desc: 'Meningococcal group B' },
  ]},
  { ageWeeks: 52, vaccines: [
    { name: 'Hib/MenC', desc: 'Haemophilus influenzae type b and meningococcal C' },
    { name: 'MMR (Dose 1)', desc: 'Measles, mumps, and rubella' },
    { name: 'PCV Booster', desc: 'Pneumococcal booster' },
    { name: 'MenB Booster', desc: 'Meningococcal group B booster' },
  ]},
  { ageWeeks: 156, vaccines: [
    { name: 'MMR (Dose 2)', desc: 'Measles, mumps, and rubella booster' },
    { name: '4-in-1 Pre-school Booster', desc: 'Diphtheria, tetanus, whooping cough, polio' },
  ]},
];

export function calculateDueDate(dob, ageWeeks) {
  return addWeeks(new Date(dob), ageWeeks);
}

// Splits a vaccination name into its base and an optional dose number.
// Accepts the formats users actually type: "6-in-1 (Dose 2)", "MMR dose 1",
// "DTaP - Dose 1", "Rotavirus #2". No dose suffix -> dose null.
function parseVaccineName(name) {
  const lower = (name || '').toLowerCase().trim();
  const m = lower.match(/(?:\(dose\s*(\d+)\)|[-–—#]?\s*dose\s*(\d+)|#\s*(\d+))\s*$/i);
  const dose = m ? parseInt(m[1] || m[2] || m[3], 10) : null;
  const base = lower.replace(/(?:\(dose\s*\d+\)|[-–—#]?\s*dose\s*\d+|#\s*\d+)\s*$/i, '').trim();
  return { base, dose };
}

// Loose base-name comparison so legacy free-text records still match
// ("6-in-1 vaccine" vs schedule base "6-in-1").
function basesMatch(a, b) {
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

// Matching rule: a record with an explicit dose number completes exactly that
// dose of the matching vaccine. A record without a dose number is a generic
// credit — it completes the EARLIEST not-yet-completed dose of that vaccine
// (so one "6-in-1" record ticks Dose 1 only, two records tick Doses 1+2, and
// single-dose vaccines behave as before). Blank names never match anything.
export function matchVaccinationsToSchedule(schedule, recordedVaccinations = [], dob) {
  if (!dob) return [];

  const now = new Date();
  const records = recordedVaccinations
    .map((v) => ({ ...parseVaccineName(v.name), record: v }))
    .filter((r) => r.base !== '');

  // Flatten the schedule with parsed names, in age order, so generic credits
  // can be assigned earliest-dose-first.
  const flat = [];
  schedule.forEach((group) => {
    group.vaccines.forEach((vaccine) => {
      const { base, dose } = parseVaccineName(vaccine.name);
      flat.push({ vaccine, group, base, dose: dose || 1 });
    });
  });

  // Pass 1: explicit dose-number matches. The map remembers WHICH record
  // completed each dose so the schedule can show "Given: <date>".
  const completedSet = new Set();
  const completedBy = new Map();
  flat.forEach((entry) => {
    const hit = records.find(
      (r) => r.dose !== null && r.dose === entry.dose && basesMatch(r.base, entry.base)
    );
    if (hit) {
      completedSet.add(entry);
      completedBy.set(entry, hit.record);
    }
  });

  // Pass 2: generic (numberless) records credit the earliest uncompleted dose.
  records
    .filter((r) => r.dose === null)
    .forEach((r) => {
      const target = flat.find((entry) => !completedSet.has(entry) && basesMatch(r.base, entry.base));
      if (target) {
        completedSet.add(target);
        completedBy.set(target, r.record);
      }
    });

  const results = [];
  schedule.forEach((group) => {
    const dueDate = calculateDueDate(dob, group.ageWeeks);
    group.vaccines.forEach((vaccine) => {
      const entry = flat.find((e) => e.vaccine === vaccine && e.group === group);
      const isCompleted = completedSet.has(entry);

      let status;
      if (isCompleted) {
        status = 'completed';
      } else if (differenceInDays(now, dueDate) > 14) {
        status = 'overdue';
      } else if (differenceInDays(dueDate, now) <= 14 && differenceInDays(dueDate, now) >= 0) {
        status = 'upcoming';
      } else if (differenceInDays(dueDate, now) > 14) {
        status = 'scheduled';
      } else {
        status = 'overdue';
      }

      const matched = completedBy.get(entry);
      results.push({
        ...vaccine,
        ageWeeks: group.ageWeeks,
        dueDate,
        status,
        // Date the matching record says it was given (null unless completed).
        givenDate: matched ? matched.date || matched.createdAt || null : null,
      });
    });
  });

  return results;
}
